import { useMemo } from 'react';
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';

import {
  DAILY_PNL_GAP_LABEL,
  dailyPnlGapReason,
  useDailyPnl,
  PredictionDailySnapshot
} from '../api/useCosts';
import { formatUsd, pnlColor } from '../utils/format';

const GAP_COLOR = '#ff9800';

const SummaryCard = ({
  label,
  value,
  color,
  note,
  title,
  ...rest
}: {
  label: string;
  value: string;
  color?: string;
  note?: string;
  title?: string;
  [key: `data-${string}`]: string | undefined;
}) => (
  <Paper
    title={title}
    sx={{
      px: 2,
      py: 1.5,
      minWidth: 180,
      backgroundColor: 'background.paper',
    }}
    {...rest}
  >
    <Typography
      sx={{
        fontSize: '0.62rem',
        textTransform: 'uppercase',
        color: 'grey.600',
        letterSpacing: 0.5,
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ fontSize: '1.15rem', fontWeight: 600, color }}>
      {value}
    </Typography>
    {note && (
      <Typography sx={{ fontSize: '0.6rem', color: GAP_COLOR, fontWeight: 600 }}>
        {note}
      </Typography>
    )}
  </Paper>
);

const ChartTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: PredictionDailySnapshot }>;
}) => {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0].payload;
  return (
    <Paper sx={{ p: 1, fontSize: '0.72rem' }}>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, mb: 0.5 }}>
        {row.date}
      </Typography>
      <Box>
        Invested Δ:{' '}
        <Box
          component="span"
          sx={{ color: pnlColor(row.costBasisDeltaUsd), fontWeight: 600 }}
        >
          {row.costBasisDeltaUsd != null
            ? formatUsd(row.costBasisDeltaUsd)
            : '—'}
        </Box>
      </Box>
      <Box>
        Cash from Trades:{' '}
        <Box
          component="span"
          sx={{ color: pnlColor(row.tradingCashDeltaUsd), fontWeight: 600 }}
        >
          {row.tradingCashDeltaUsd != null
            ? formatUsd(row.tradingCashDeltaUsd)
            : '—'}
        </Box>
      </Box>
      <Box>
        Daily P&amp;L:{' '}
        <Box
          component="span"
          sx={{ color: pnlColor(row.dailyPnlUsd), fontWeight: 600 }}
        >
          {row.dailyPnlUsd != null ? formatUsd(row.dailyPnlUsd) : '—'}
        </Box>
      </Box>
    </Paper>
  );
};

export const DailyPnlTab = () => {
  const { data } = useDailyPnl();

  const rows = data ?? [];

  // The window every card below aggregates over. A day the backend could not
  // attribute (first snapshot, or an account added mid-life) contributes
  // nothing to the sum, so the sum is over FEWER days than the label claims —
  // and a total that omits days while presenting as lifetime P&L is the
  // unknown-as-zero this screen exists to kill. The gap is counted here and
  // shown next to the number.
  const summary = useMemo(() => {
    let total = 0;
    let attributedDays = 0;
    let unknownDays = 0;
    let best: PredictionDailySnapshot | null = null;
    let worst: PredictionDailySnapshot | null = null;
    let today: PredictionDailySnapshot | null = null;
    for (const r of rows) {
      if (r.dailyPnlUsd != null) {
        total += r.dailyPnlUsd;
        attributedDays++;
        if (best == null || (r.dailyPnlUsd ?? 0) > (best.dailyPnlUsd ?? 0)) {
          best = r;
        }
        if (worst == null || (r.dailyPnlUsd ?? 0) < (worst.dailyPnlUsd ?? 0)) {
          worst = r;
        }
      } else {
        unknownDays++;
      }
    }
    // The latest row by date == "today's" snapshot (rows arrive sorted asc).
    if (rows.length > 0) today = rows[rows.length - 1];
    return { total, attributedDays, unknownDays, best, worst, today };
  }, [rows]);

  const gapNote =
    summary.unknownDays > 0
      ? `ventana incompleta · ${summary.unknownDays} día${
          summary.unknownDays === 1 ? '' : 's'
        } sin atribuir`
      : undefined;
  const gapTitle =
    summary.unknownDays > 0
      ? `${summary.unknownDays} día(s) de la serie no tienen P&L atribuido (primer snapshot, o un cambio en el conjunto de cuentas). No entran en estas cifras: el total es sobre los ${summary.attributedDays} día(s) restantes, no sobre toda la vida.`
      : undefined;

  // Chart consumes ascending-by-date data; the table renders descending.
  const tableRows = useMemo(() => [...rows].reverse(), [rows]);

  return (
    <Box>
      <Stack direction="row" spacing={1.5} flexWrap="wrap" mb={2}>
        <SummaryCard
          label="Total P&L Since Day 0"
          // No attributed day at all means there is no total to state — a `$0`
          // here would be a lifetime P&L invented out of nothing.
          value={
            summary.attributedDays > 0 ? formatUsd(summary.total) : '—'
          }
          color={
            summary.attributedDays > 0 ? pnlColor(summary.total) : undefined
          }
          note={gapNote}
          title={gapTitle}
          data-total-pnl-window={
            summary.unknownDays > 0 ? 'incomplete' : 'complete'
          }
          data-total-pnl-unknown-days={String(summary.unknownDays)}
        />
        <SummaryCard
          label="Today's P&L"
          value={
            summary.today?.dailyPnlUsd != null
              ? formatUsd(summary.today.dailyPnlUsd)
              : '—'
          }
          color={pnlColor(summary.today?.dailyPnlUsd ?? null)}
        />
        <SummaryCard
          label="Best Day"
          value={
            summary.best
              ? `${formatUsd(summary.best.dailyPnlUsd)} (${summary.best.date})`
              : '—'
          }
          color="#4caf50"
          title={gapTitle}
          data-extremes-window={
            summary.unknownDays > 0 ? 'incomplete' : 'complete'
          }
        />
        <SummaryCard
          label="Worst Day"
          value={
            summary.worst
              ? `${formatUsd(summary.worst.dailyPnlUsd)} (${summary.worst.date})`
              : '—'
          }
          color="#f44336"
          title={gapTitle}
          data-extremes-window={
            summary.unknownDays > 0 ? 'incomplete' : 'complete'
          }
        />
      </Stack>

      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Typography
          sx={{
            fontSize: '0.72rem',
            textTransform: 'uppercase',
            color: 'grey.600',
            mb: 1,
          }}
        >
          Daily P&amp;L
        </Typography>
        {rows.length === 0 ? (
          <Box
            sx={{
              height: 280,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography sx={{ fontSize: '0.82rem', color: 'grey.600' }}>
              No daily snapshots yet — first row writes at 23:59 UTC
            </Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart
              data={rows}
              margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
            >
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: '#888' }}
                stroke="#444"
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#888' }}
                stroke="#444"
                tickFormatter={(v: number) => formatUsd(v)}
                width={70}
              />
              <Tooltip content={<ChartTooltip />} />
              <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" />
              <Line
                type="monotone"
                dataKey="dailyPnlUsd"
                stroke="#2196f3"
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Paper>

      <Paper sx={{ p: 1.5 }}>
        <Typography
          sx={{
            fontSize: '0.72rem',
            textTransform: 'uppercase',
            color: 'grey.600',
            mb: 1,
          }}
        >
          History
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell align="right">Open Value</TableCell>
              <TableCell align="right">Invested Δ</TableCell>
              <TableCell align="right">Cash from Trades</TableCell>
              <TableCell align="right">Cashflows</TableCell>
              <TableCell align="right">Daily P&amp;L</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableRows.map((r, i) => {
              // `tableRows` is descending by date, so the PREVIOUS day is the
              // next element — the row a day-over-day diff is taken against.
              const previous = tableRows[i + 1];
              const gap = dailyPnlGapReason(r, previous);
              return (
              <TableRow key={r.date}>
                <TableCell>{r.date}</TableCell>
                <TableCell align="right">
                  {formatUsd(r.openPositionsMarkValueUsd)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: pnlColor(r.costBasisDeltaUsd) }}
                >
                  {r.costBasisDeltaUsd != null
                    ? formatUsd(r.costBasisDeltaUsd)
                    : '—'}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: pnlColor(r.tradingCashDeltaUsd) }}
                >
                  {r.tradingCashDeltaUsd != null
                    ? formatUsd(r.tradingCashDeltaUsd)
                    : '—'}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: pnlColor(r.cashflowsUsd) }}
                >
                  {formatUsd(r.cashflowsUsd)}
                </TableCell>
                <TableCell
                  align="right"
                  data-daily-pnl-gap={gap ?? undefined}
                  title={gap ? DAILY_PNL_GAP_LABEL[gap] : undefined}
                  sx={{
                    color: gap === 'scope-changed' ? '#ff9800' : pnlColor(r.dailyPnlUsd),
                    fontWeight: 600
                  }}
                >
                  {r.dailyPnlUsd != null
                    ? formatUsd(r.dailyPnlUsd)
                    : gap === 'scope-changed'
                      ? 'no comparable'
                      : '—'}
                </TableCell>
              </TableRow>
              );
            })}
            {tableRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography sx={{ fontSize: '0.75rem', color: 'grey.600' }}>
                    No daily snapshots yet
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <Typography
          variant="caption"
          sx={{ color: 'grey.600', display: 'block', mt: 1 }}
        >
          Invested Δ + Cash from Trades = Daily P&amp;L — realized only.
          Open-position price moves are excluded: a day with no closes is $0
          P&amp;L no matter how the open markets drift. (Open Value is the
          live mark, shown for context only.)
        </Typography>
      </Paper>
    </Box>
  );
};
